import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]";

const EMPLOYMENT_TYPES = ["full_time", "part_time", "per_diem", "contract", "temporary", "internship"];
const WORK_ARRANGEMENTS = ["onsite", "remote", "hybrid"];
const STATUSES = ["draft", "published", "closed"];

const empty = {
  title: "", department: "", location: "", work_arrangement: "onsite",
  employment_type: "full_time", summary: "", description: "",
  responsibilities: "", qualifications: "", preferred_qualifications: "",
  schedule: "", compensation_range: "", application_deadline: "",
  is_upcoming: false,
  status: "draft", internal_notes: "",
};

const linesToList = (s) => (s || "").split("\n").map((l) => l.trim()).filter(Boolean);
const listToLines = (a) => (a || []).join("\n");

export default function AdminJobEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/jobs/${id}`)
      .then((r) => {
        const j = r.data;
        setForm({
          title: j.title || "", department: j.department || "", location: j.location || "",
          work_arrangement: j.work_arrangement || "onsite", employment_type: j.employment_type || "full_time",
          summary: j.summary || "", description: j.description || "",
          responsibilities: listToLines(j.responsibilities),
          qualifications: listToLines(j.qualifications),
          preferred_qualifications: listToLines(j.preferred_qualifications),
          schedule: j.schedule || "", compensation_range: j.compensation_range || "",
          application_deadline: j.application_deadline || "",
          is_upcoming: !!j.is_upcoming,
          status: j.status || "draft",
          internal_notes: j.internal_notes || "",
        });
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        navigate("/care-portal/admin/jobs");
      })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        department: form.department.trim() || undefined,
        location: form.location.trim(),
        work_arrangement: form.work_arrangement,
        employment_type: form.employment_type,
        summary: form.summary.trim(),
        description: form.description.trim(),
        responsibilities: linesToList(form.responsibilities),
        qualifications: linesToList(form.qualifications),
        preferred_qualifications: linesToList(form.preferred_qualifications),
        schedule: form.schedule.trim() || undefined,
        compensation_range: form.compensation_range.trim() || undefined,
        application_deadline: form.application_deadline.trim() || undefined,
        is_upcoming: !!form.is_upcoming,
        status: nextStatus || form.status,
        internal_notes: form.internal_notes.trim() || undefined,
      };
      if (isNew) {
        const r = await api.post("/admin/jobs", payload);
        toast.success("Job posting created");
        navigate(`/care-portal/admin/jobs/${r.data.id}/edit`);
      } else {
        await api.patch(`/admin/jobs/${id}`, payload);
        toast.success("Saved");
        setForm((s) => ({ ...s, status: payload.status }));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-12 text-center text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="admin-job-edit">
      <button onClick={() => navigate("/care-portal/admin/jobs")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-4"><ArrowLeft className="w-4 h-4" /> Back to postings</button>
      <h2 className="font-heading text-2xl tracking-tight text-slate-900">{isNew ? "New Job Posting" : "Edit Job Posting"}</h2>
      <p className="mt-1 text-sm text-slate-500">Draft postings are not visible publicly. Publish deliberately.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5">
        <Row2>
          <Field label="Title *"><input required data-testid="jobfield-title" value={form.title} onChange={update("title")} className={inputCls} /></Field>
          <Field label="Department / service area"><input data-testid="jobfield-department" value={form.department} onChange={update("department")} className={inputCls} /></Field>
        </Row2>
        <Row2>
          <Field label="Location *"><input required data-testid="jobfield-location" value={form.location} onChange={update("location")} className={inputCls} placeholder="e.g. Sacramento, CA or California" /></Field>
          <Row2>
            <Field label="Work arrangement"><select data-testid="jobfield-arrangement" value={form.work_arrangement} onChange={update("work_arrangement")} className={inputCls}>{WORK_ARRANGEMENTS.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
            <Field label="Employment type"><select data-testid="jobfield-employment-type" value={form.employment_type} onChange={update("employment_type")} className={inputCls}>{EMPLOYMENT_TYPES.map((v) => <option key={v} value={v}>{v.replace("_", " ")}</option>)}</select></Field>
          </Row2>
        </Row2>
        <Field label="Short summary *"><textarea rows={2} required data-testid="jobfield-summary" value={form.summary} onChange={update("summary")} className={inputCls} /></Field>
        <Field label="Full description *"><textarea rows={8} required data-testid="jobfield-description" value={form.description} onChange={update("description")} className={inputCls} /></Field>
        <Field label="Responsibilities (one per line)"><textarea rows={5} data-testid="jobfield-responsibilities" value={form.responsibilities} onChange={update("responsibilities")} className={inputCls} /></Field>
        <Field label="Qualifications (one per line)"><textarea rows={5} data-testid="jobfield-qualifications" value={form.qualifications} onChange={update("qualifications")} className={inputCls} /></Field>
        <Field label="Preferred qualifications (one per line)"><textarea rows={4} data-testid="jobfield-preferred" value={form.preferred_qualifications} onChange={update("preferred_qualifications")} className={inputCls} /></Field>
        <Row2>
          <Field label="Schedule (optional)"><input data-testid="jobfield-schedule" value={form.schedule} onChange={update("schedule")} className={inputCls} /></Field>
          <Field label="Compensation range (optional — only if entered)"><input data-testid="jobfield-compensation" value={form.compensation_range} onChange={update("compensation_range")} className={inputCls} /></Field>
        </Row2>
        <Row2>
          <Field label="Application deadline (optional)"><input data-testid="jobfield-deadline" value={form.application_deadline} onChange={update("application_deadline")} className={inputCls} placeholder="e.g. March 31, 2026" /></Field>
          <Field label="Status"><select data-testid="jobfield-status" value={form.status} onChange={update("status")} className={inputCls}>{STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}</select></Field>
        </Row2>
        <Field label="Internal admin notes"><textarea rows={3} data-testid="jobfield-notes" value={form.internal_notes} onChange={update("internal_notes")} className={inputCls} /></Field>

        <label className="inline-flex items-center gap-2 text-sm text-slate-700 pt-2 border-t border-slate-200 pt-4 w-full">
          <input type="checkbox" checked={form.is_upcoming} onChange={(e) => setForm((s) => ({ ...s, is_upcoming: e.target.checked }))} data-testid="jobfield-upcoming" className="h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
          <span>Mark as <strong>Upcoming Position</strong> — shows an "Accepting Applications — Upcoming Position" badge on the public posting and adds "not guaranteed to start immediately" language.</span>
        </label>

        <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-slate-200">
          <button onClick={() => save("draft")} disabled={saving} data-testid="save-draft" className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Save as Draft</button>
          <button onClick={() => save("published")} disabled={saving} data-testid="save-publish" className="px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]">Save & Publish</button>
          {!isNew && form.status === "published" && <button onClick={() => save("closed")} disabled={saving} data-testid="save-close" className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700">Close Posting</button>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><span className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">{label}</span>{children}</label>);
}
function Row2({ children }) { return <div className="grid sm:grid-cols-2 gap-5">{children}</div>; }
